/**
 * 画面の経路。
 *
 * ★ **いまいる場所を URL に置く。** 状態で持つと、画面を共有できず、
 * 再読み込みで失われ、戻るボタンが効かない。
 *
 * ```
 * /                                  テナントの居場所（既定のタブ）
 * /t/:tenantId                       テナントの居場所
 * /t/:tenantId/environments          その中のタブ
 * /t/:tenantId/p/:projectId          プロジェクトの居場所
 * /t/:tenantId/p/:projectId/bundles  その中のタブ
 * ```
 *
 * ★ ライブラリは足さない。`pushState` と小さなパーサで足りる。
 * Worker 側は `not_found_handling: "single-page-application"` なので、
 * 深い経路を直接開いても index.html が返る（`run_worker_first` に
 * `/t/` を**足さないこと**）。
 */

export type Tab =
  | "chat"
  | "runs"
  | "api"
  | "logs"
  | "metrics"
  | "audit"
  | "keys"
  | "credentials"
  | "environments"
  | "bundles"
  | "projects"
  | "tenants"
  | "cloud-access"
  | "support";

export const ALL_TABS: readonly Tab[] = [
  "chat",
  "runs",
  "api",
  "logs",
  "metrics",
  "audit",
  "keys",
  "credentials",
  "environments",
  "bundles",
  "projects",
  "tenants",
  "cloud-access",
  "support",
];

const TAB_SET = new Set<string>(ALL_TABS);

export const DEFAULT_TAB: Tab = "chat";

export interface Route {
  /** URL に書かれていたテナント。無ければ null（ログイン中のテナントを使う）。 */
  readonly tenantId: string | null;
  /** いまいるプロジェクト。null はテナント直下。 */
  readonly projectId: string | null;
  readonly tab: Tab;
  /** Run の詳細を開いているとき。 */
  readonly runId: string | null;
}

export const ROOT_ROUTE: Route = {
  tenantId: null,
  projectId: null,
  tab: DEFAULT_TAB,
  runId: null,
};

/**
 * 経路を読む。
 *
 * ★ 知らない経路は既定のタブに落とす。404 の画面を作るより、
 * 入り口に戻す方が使う側にとって親切。
 */
export function parseRoute(pathname: string): Route {
  const parts = pathname.split("/").filter((part) => part !== "");

  let tenantId: string | null = null;
  let projectId: string | null = null;
  let index = 0;

  if (parts[0] === "t" && parts[1] !== undefined) {
    tenantId = decodeURIComponent(parts[1]);
    index = 2;
    if (parts[2] === "p" && parts[3] !== undefined) {
      projectId = decodeURIComponent(parts[3]);
      index = 4;
    }
  }

  const tabPart = parts[index];
  const tab: Tab = tabPart !== undefined && TAB_SET.has(tabPart) ? (tabPart as Tab) : DEFAULT_TAB;

  // Run の詳細は `runs/:runId`。
  const runId =
    tab === "runs" && parts[index + 1] !== undefined
      ? decodeURIComponent(parts[index + 1]!)
      : null;

  return { tenantId, projectId, tab, runId };
}

/** 経路を書く。`parseRoute` と往復する。 */
export function buildPath(route: Route): string {
  const parts: string[] = [];
  if (route.tenantId !== null) {
    parts.push("t", encodeURIComponent(route.tenantId));
    if (route.projectId !== null) parts.push("p", encodeURIComponent(route.projectId));
  }
  parts.push(route.tab);
  if (route.tab === "runs" && route.runId !== null) parts.push(encodeURIComponent(route.runId));
  return `/${parts.join("/")}`;
}
